import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface Video {
  id: number;
  title: string;
  description?: string;
  youtube_url: string;
  thumbnail_url?: string;
  is_active: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

const VideoManager: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const [videoForm, setVideoForm] = useState({
    title: '',
    description: '',
    youtube_url: '',
    thumbnail_url: '',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cms/videos');
      setVideos(response.data);
    } catch (error: any) {
      console.error('Error fetching videos:', error);
      alert('Error loading videos: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await api.put(`/cms/videos/${editingVideo.id}`, videoForm);
      } else {
        await api.post('/cms/videos', videoForm);
      }
      setShowForm(false);
      setEditingVideo(null);
      setVideoForm({
        title: '',
        description: '',
        youtube_url: '',
        thumbnail_url: '',
        is_active: true,
        order: 0
      });
      fetchVideos();
    } catch (error: any) {
      console.error('Error saving video:', error);
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.youtube_url ? error.response.data.youtube_url[0] : '') ||
                          error.message || 
                          'Unknown error';
      alert('Error saving video: ' + errorMessage);
    }
  };

  const handleEdit = (video: Video) => {
    setEditingVideo(video);
    setVideoForm({
      title: video.title,
      description: video.description || '',
      youtube_url: video.youtube_url,
      thumbnail_url: video.thumbnail_url || '',
      is_active: video.is_active,
      order: video.order
    });
    setShowForm(true);
  };

  const handleDelete = async (videoId: number) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await api.delete(`/cms/videos/${videoId}`);
        fetchVideos();
      } catch (error: any) {
        console.error('Error deleting video:', error);
        alert('Error deleting video: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      }
    }
  };

  const extractYoutubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const getEmbedUrl = (youtubeUrl: string): string | null => {
    const videoId = extractYoutubeId(youtubeUrl);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const getThumbnailUrl = (youtubeUrl: string): string | null => {
    const videoId = extractYoutubeId(youtubeUrl);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const handlePreview = (video: Video) => {
    setPreviewVideo(video);
    setShowPreview(true);
  };

  const toggleActive = async (video: Video) => {
    try {
      await api.put(`/cms/videos/${video.id}`, {
        ...video,
        is_active: !video.is_active
      });
      fetchVideos();
    } catch (error: any) {
      console.error('Error updating video:', error);
      alert('Error updating video: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="loading">Loading videos...</div>;
  }

  return (
    <div className="cms-container">
      <div className="cms-header">
        <h2>Video Gallery Management</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            setEditingVideo(null);
            setVideoForm({
              title: '',
              description: '',
              youtube_url: '',
              thumbnail_url: '',
              is_active: true,
              order: 0
            });
            setShowForm(true);
          }}
        >
          <i className="fas fa-plus"></i> Add New Video
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>{editingVideo ? 'Edit Video' : 'Add New Video'}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={videoForm.title}
                  onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                  required
                  placeholder="Enter video title"
                />
              </div>

              <div className="form-group">
                <label>YouTube URL *</label>
                <input
                  type="url"
                  value={videoForm.youtube_url}
                  onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
                  required
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <small className="form-text">Paste the full YouTube video URL</small>
                {videoForm.youtube_url && getEmbedUrl(videoForm.youtube_url) && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ 
                      position: 'relative', 
                      paddingBottom: '56.25%', 
                      height: 0, 
                      overflow: 'hidden',
                      maxWidth: '100%',
                      background: '#000'
                    }}>
                      <iframe
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%'
                        }}
                        src={getEmbedUrl(videoForm.youtube_url) || ''}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Preview"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={videoForm.description}
                  onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                  rows={4}
                  placeholder="Enter video description (optional)"
                />
              </div>

              <div className="form-group">
                <label>Custom Thumbnail URL (Optional)</label>
                <input
                  type="url"
                  value={videoForm.thumbnail_url}
                  onChange={(e) => setVideoForm({ ...videoForm, thumbnail_url: e.target.value })}
                  placeholder="https://example.com/thumbnail.jpg"
                />
                <small className="form-text">Leave empty to use YouTube's default thumbnail</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={videoForm.order}
                    onChange={(e) => setVideoForm({ ...videoForm, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={videoForm.is_active}
                      onChange={(e) => setVideoForm({ ...videoForm, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && previewVideo && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h3>{previewVideo.title}</h3>
              <button className="close-btn" onClick={() => setShowPreview(false)}>&times;</button>
            </div>
            <div style={{ 
              position: 'relative', 
              paddingBottom: '56.25%', 
              height: 0, 
              overflow: 'hidden',
              maxWidth: '100%',
              background: '#000'
            }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
                src={getEmbedUrl(previewVideo.youtube_url) || ''}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={previewVideo.title}
              />
            </div>
            {previewVideo.description && (
              <div style={{ padding: '15px', marginTop: '15px' }}>
                <p>{previewVideo.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="videos-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px',
        marginTop: '20px'
      }}>
        {videos.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            <p>No videos found. Add your first video to get started!</p>
          </div>
        ) : (
          videos.map((video) => {
            const thumbnailUrl = video.thumbnail_url || getThumbnailUrl(video.youtube_url);
            return (
              <div key={video.id} className="video-card" style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div 
                  style={{
                    position: 'relative',
                    paddingBottom: '56.25%',
                    height: 0,
                    overflow: 'hidden',
                    background: '#000',
                    cursor: 'pointer'
                  }}
                  onClick={() => handlePreview(video)}
                >
                  {thumbnailUrl ? (
                    <img 
                      src={thumbnailUrl} 
                      alt={video.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#fff',
                      fontSize: '48px'
                    }}>
                      <i className="fas fa-video"></i>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(0,0,0,0.7)',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '24px'
                  }}>
                    <i className="fas fa-play"></i>
                  </div>
                </div>
                <div style={{ padding: '15px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>{video.title}</h4>
                  {video.description && (
                    <p style={{ 
                      margin: '0 0 10px 0', 
                      fontSize: '14px', 
                      color: '#666',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {video.description}
                    </p>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '10px'
                  }}>
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#999',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <i className={`fas fa-${video.is_active ? 'check-circle' : 'times-circle'}`} 
                         style={{ color: video.is_active ? '#28a745' : '#dc3545' }}></i>
                      {video.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      Order: {video.order}
                    </span>
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    marginTop: '15px',
                    flexWrap: 'wrap'
                  }}>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={() => handlePreview(video)}
                      style={{ flex: 1, minWidth: '80px' }}
                    >
                      <i className="fas fa-play"></i> Preview
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(video)}
                      style={{ flex: 1, minWidth: '80px' }}
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-warning"
                      onClick={() => toggleActive(video)}
                      style={{ flex: 1, minWidth: '80px' }}
                    >
                      <i className={`fas fa-${video.is_active ? 'eye-slash' : 'eye'}`}></i>
                      {video.is_active ? 'Hide' : 'Show'}
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(video.id)}
                      style={{ flex: 1, minWidth: '80px' }}
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VideoManager;

